import React, { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import { COLOR_DENIM } from 'popTheme';
import Disclaimer from 'components/common/Disclaimer';
import ContentLoader from 'components/checkIn/ContentLoader';
import Header from 'components/checkIn/Header';
import SelfAssessment from './questionnaire/SelfAssessment';
import { resetStatus } from 'redux/checkIn/actions';
import { autoSaveCheckInStatus, loadCheckInStatus } from 'redux/checkIn/sagas';
import {
  selectAlreadySubmitted,
  selectLastLoadingError,
  selectLoading,
  selectSurveyStarted,
} from 'redux/checkIn/selectors';

export const CheckIn = () => {
  const { paragraph1, paragraph2 } = useCopy();
  const dispatch = useDispatch();

  const autoSave = useCallback(() => {
    return setInterval(() => dispatch(autoSaveCheckInStatus()), 3000);
  }, [dispatch]);

  useEffect(() => {
    dispatch(loadCheckInStatus());
    const intervalID = autoSave();
    return () => clearInterval(intervalID);
  }, [dispatch, autoSave]);

  useEffect(() => {
    return () => {
      dispatch(resetStatus());
    };
  }, [dispatch]);

  const isAlreadySubmitted = useSelector(selectAlreadySubmitted);
  const isLoading = useSelector(selectLoading);
  const isLoadingFailure = useSelector(selectLastLoadingError);
  const isStarted = useSelector(selectSurveyStarted);

  return (
    <Grid container direction="column" data-testid="check-in">
      <Grid item p="3.75rem">
        <Header />
      </Grid>
      <Grid item sx={{ bgcolor: COLOR_DENIM, pb: '3.75rem', height: '100%', width: 'inherit' }}>
        <ContentLoader
          isAlreadySubmitted={isAlreadySubmitted}
          isLoading={isLoading}
          isLoadingFailure={isLoadingFailure}
          isStarted={isStarted}
        >
          <SelfAssessment />
        </ContentLoader>
      </Grid>
      <Grid item>
        <Disclaimer paragraph1={paragraph1} paragraph2={paragraph2} />
      </Grid>
    </Grid>
  );
};

const useCopy = () => {
  const intl = useIntl();
  const messages = defineMessages({
    paragraph1: {
      id: 'checkIn.disclaimer.paragraph1',
      defaultMessage:
        'Please note, this assessment does not replace seeing a healthcare professional.',
    },
    paragraph2: {
      id: 'checkIn.disclaimer.paragraph2',
      defaultMessage:
        'If you need support with mood, worry, sleep, and pain, contact your healthcare team today.',
    },
  });

  const paragraph1 = intl.formatMessage(messages.paragraph1);
  const paragraph2 = intl.formatMessage(messages.paragraph2);

  return { paragraph1, paragraph2 };
};
